import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/lib/useTheme';

/**
 * A real switch, not a button that says what it will do next.
 *
 * role="switch" with aria-checked is the whole reason to build this rather than style
 * a checkbox: a screen reader announces "dark mode, on" instead of reading a label
 * that flips meaning every time you press it.
 */
export function ThemeSwitch({ compact = false }: { compact?: boolean }) {
  const { isDark, toggle } = useTheme();

  if (compact) {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={isDark}
        aria-label="Dark mode"
        title={isDark ? 'Switch to light' : 'Switch to dark'}
        onClick={toggle}
        className="flex size-10 items-center justify-center rounded-lg transition-colors hover:opacity-70"
        style={{ color: 'var(--text-muted)' }}
      >
        {isDark ? <Sun size={17} /> : <Moon size={17} />}
      </button>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggle}
      className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm transition-colors hover:opacity-80"
      style={{ color: 'var(--text-muted)' }}
    >
      {isDark ? <Sun size={17} /> : <Moon size={17} />}
      <span className="flex-1 text-left">Dark mode</span>

      {/*
        Sized in pixels, not Tailwind steps.
        Track 36, thumb 16, inset 2 — so the travel is exactly 36 - 16 - 2 - 2 = 16px.
        Deriving that from rem classes is how the thumb ended up half outside the
        track: a value that looks right at one root font size is wrong at another.
      */}
      <span
        aria-hidden
        className="relative shrink-0 rounded-full transition-colors duration-200"
        style={{
          width: 36,
          height: 20,
          background: isDark ? 'var(--accent)' : 'var(--border-strong)',
        }}
      >
        <span
          className="absolute rounded-full transition-transform duration-200 ease-out"
          style={{
            width: 16,
            height: 16,
            top: 2,
            left: 2,
            // White in both states: the thumb reads as the moving part, and tying it
            // to --surface made it vanish into the page the moment dark went black.
            background: '#fff',
            boxShadow: '0 1px 2px rgb(0 0 0 / 0.35)',
            transform: isDark ? 'translateX(16px)' : 'translateX(0)',
          }}
        />
      </span>
    </button>
  );
}
